import { useMemo, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  formatPeriod,
  getPeriodsForThePastMonths,
  PastWinnersPeriod,
  IChallengePastWinner,
} from "@ascendnow/shared";

import AscGenericCard from "../AscGenericCard/AscGenericCard";
import styles from "./AscPastWinnersCard.module.scss";
import AscAvatar from "../../AscAvatar/AscAvatar";
import {
  FirstPlaceBadge,
  SecondPlaceBadge,
  ThirdPlaceBadge,
} from "../../AscIllustrations";
import { SchoolRepresentativeIcon } from "../../AscIcons";
import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";
// import AscTag from "../../AscTag/AscTag";
import AscButton from "../../AscButton/AscButton";
import AscDropdown from "../../AscDropdown/AscDropdown";

import AscLink from "../../AscLink/AscLink";

dayjs.extend(relativeTime);

export interface AscPastWinnersCardProps {
  title: string;
  pastWinners: IChallengePastWinner[];
  className?: string;
  loading?: boolean;
  onClick?: () => void;
  showPeriod?: boolean;
  onPeriodChange?: (period: PastWinnersPeriod) => void;
  seeMorePath?: string;
}

export default function AscPastWinnersCard({
  title,
  pastWinners,
  className,
  loading = false,
  onClick,
  showPeriod = false,
  onPeriodChange,
  seeMorePath,
}: AscPastWinnersCardProps) {
  const [period, setPeriod] = useState(() => formatPeriod());
  const periods = useMemo(() => getPeriodsForThePastMonths(6), []);

  const placeIconHandler = (place: number) => {
    switch (place) {
      case 1:
        return <FirstPlaceBadge />;
      case 2:
        return <SecondPlaceBadge />;
      case 3:
        return <ThirdPlaceBadge />;
      default:
        return <span className={styles.place}>{place}.</span>;
    }
  };

  function handlePeriodChange(period: PastWinnersPeriod) {
    setPeriod(period);
    onPeriodChange?.(period);
  }

  // const getRankTag = (rank: number) => {
  //   switch (rank) {
  //     case 1:
  //       return <AscTag type={"gold"} label={"Gold"} size={"small"} />;
  //     case 2:
  //       return <AscTag type={"silver"} label={"Silver"} size={"small"} />;
  //     case 3:
  //       return <AscTag type={"bronze"} label={"Bronze"} size={"small"} />;
  //     default:
  //       return null;
  //   }
  // };
  return (
    <AscGenericCard
      className={classNames(styles.ground, className)}
      titleClassName={styles.cardTitle}
      bodyClassName={styles.cardBody}
      contentClassName={styles.cardContent}
      title={
        <div className={styles.header}>
          <div className={styles.cardTitle}>{title}</div>
          {showPeriod ? (
            <AscDropdown title={period?.label}>
              {periods?.map((period) => (
                <AscDropdown.Option
                  key={period?.value}
                  onClick={() => handlePeriodChange(period)}
                >
                  {period?.label}
                </AscDropdown.Option>
              ))}
            </AscDropdown>
          ) : null}
        </div>
      }
      coverImageUrl={null}
      loading={loading}
      data-testid="asc-past-winners-card"
      footer={
        <AscLink innerWrapperTag={null} href={seeMorePath}>
          <AscButton
            variant="secondary"
            fullWidth
            size="small"
            onClick={onClick}
            disabled={!pastWinners?.length}
          >
            See full list
          </AscButton>
        </AscLink>
      }
    >
      {pastWinners?.length ? (
        pastWinners?.map((winner) => (
          <div className={styles.winnerItem}>
            <div className={styles.left}>
              {placeIconHandler(winner?.placement)}
              <AscAvatar src={winner?.image} flag={winner?.flag} />
              <div className={styles.details}>
                <div className={styles.nameTag}>
                  <AscParagraph className={styles.name}>
                    {winner?.name}
                  </AscParagraph>
                  {/* {winner?.rank && getRankTag(winner?.rank)} */}
                </div>
                <div className={styles.ageSchool}>
                  <AscParagraph>
                    Age{" "}
                    {Math.floor(dayjs().diff(dayjs(winner?.birthDate), "year"))}
                  </AscParagraph>
                  <AscParagraph>|</AscParagraph>
                  <div className={styles.school}>
                    <SchoolRepresentativeIcon />
                    <AscParagraph>{winner?.school}</AscParagraph>
                  </div>
                </div>
              </div>
            </div>
            <AscParagraph className={styles.score}>
              {winner?.score}
            </AscParagraph>
          </div>
        ))
      ) : (
        <div className={styles.noWinners}>No winners yet</div>
      )}
    </AscGenericCard>
  );
}
